import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, CaretDown, CaretUp, CheckCircle, CircleNotch, WarningCircle, File } from '@phosphor-icons/react';
import { ProcessingItem } from '../contexts/UploadContext';

interface ProcessingProgressProps {
  items: ProcessingItem[];
  onClose: () => void;
  title?: string;
  completeTitle?: string;
  failedTitle?: string;
  bottomOffset?: number;
}

// Re-export for backward compatibility
export type { ProcessingItem };

export const ProcessingProgress: React.FC<ProcessingProgressProps> = ({
  items,
  onClose,
  title,
  completeTitle,
  failedTitle,
  bottomOffset,
}) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = React.useState(false);

  if (items.length === 0) return null;

  const pendingItems = items.filter(d => d.status === 'pending');
  const processingItems = items.filter(d => d.status === 'processing');
  const completedItems = items.filter(d => d.status === 'complete');
  const errorItems = items.filter(d => d.status === 'error');

  const activeCount = pendingItems.length + processingItems.length;
  const totalCount = items.length;
  const processedCount = completedItems.length + errorItems.length;

  const getHeaderText = () => {
    if (processingItems.length > 0 || pendingItems.length > 0) {
      return title || t('processing.extracting', 'Извлечение данных...');
    }
    if (errorItems.length > 0 && completedItems.length === 0) {
      return failedTitle || t('processing.failed', 'Ошибка обработки');
    }
    return completeTitle || t('processing.complete', 'Обработка завершена');
  };

  const content = (
    <div className="fixed right-0 z-[9999] w-[360px] bg-card rounded-tl-2xl shadow-2xl border border-border border-b-0 border-r-0 overflow-hidden" style={{ bottom: bottomOffset ? `${bottomOffset}px` : 0 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
        <div className="flex items-center gap-3">
          {activeCount === 0 && errorItems.length === 0 && (
            <CheckCircle className="h-4 w-4 text-emerald-500" weight="fill" />
          )}
          {activeCount === 0 && errorItems.length > 0 && completedItems.length > 0 && (
            <WarningCircle className="h-4 w-4 text-amber-500" weight="fill" />
          )}
          <span className="font-medium text-sm text-foreground">
            {getHeaderText()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Progress counter */}
          <span className="text-xs text-muted-foreground mr-2">
            {processedCount}/{totalCount}
          </span>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-muted/50 rounded-lg transition-colors"
          >
            {isMinimized ? (
              <CaretUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <CaretDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {activeCount === 0 && (
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-muted/50 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-muted">
        <div
          className="h-full bg-amber-500 transition-all duration-300"
          style={{ width: `${(processedCount / totalCount) * 100}%` }}
        />
      </div>

      {/* Content */}
      {!isMinimized && (
        <div className="max-h-[250px] overflow-y-auto bg-card">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 last:border-b-0"
            >
              {/* Icon */}
              <div className="flex-shrink-0">
                <File className="h-4 w-4 text-muted-foreground" />
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">
                  {item.name}
                </p>
                {item.status === 'error' && item.error && (
                  <p className="text-xs text-red-500 truncate">{item.error}</p>
                )}
              </div>

              {/* Status indicator */}
              <div className="flex-shrink-0">
                {item.status === 'pending' && (
                  <span className="text-xs text-muted-foreground">
                    {t('processing.pending', 'В очереди')}
                  </span>
                )}
                {item.status === 'processing' && (
                  <CircleNotch className="h-4 w-4 text-amber-500 animate-spin" />
                )}
                {item.status === 'complete' && (
                  <CheckCircle className="h-4 w-4 text-emerald-500" weight="fill" />
                )}
                {item.status === 'error' && (
                  <WarningCircle className="h-4 w-4 text-red-500" weight="fill" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer with summary */}
      {!isMinimized && items.length > 0 && (
        <div className="px-4 py-2 bg-muted/30 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {activeCount > 0 && t('processing.remaining', 'Осталось: {{count}}', { count: activeCount })}
            {activeCount > 0 && completedItems.length > 0 && ' • '}
            {completedItems.length > 0 && t('processing.completed', 'Готово: {{count}}', { count: completedItems.length })}
            {errorItems.length > 0 && ` • ${t('processing.errors', 'Ошибок: {{count}}', { count: errorItems.length })}`}
          </p>
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
};

export default ProcessingProgress;
